import React, { useState, useEffect } from 'react';
import { 
  Users, MessageCircle, Briefcase, Clock, Play, Square, UserCheck, AlertTriangle, 
  ChevronRight, LogOut, Calendar, Timer, BarChart3, Bell, Megaphone, Heart, Pin, 
  X, AlertCircle, History, TrendingUp, Zap, Award, GraduationCap, FolderOpen,
  Target, Flame, Coffee, Sun, Moon, Sparkles, Activity, ArrowUpRight, CheckCircle,
  Star, Trophy, Rocket, Gift, Shield, Phone, Video, FileText, ClipboardList,
  Lightbulb, ThumbsUp, PartyPopper, CircleDot, Layers, ArrowUp, ArrowDown,
  CalendarDays, Inbox, MoreHorizontal, ExternalLink, RefreshCw, Settings,
  TrendingDown, Percent, Building2, Clock3, PieChart, LayoutGrid, Briefcase as Job,
  Cake, MapPin, Quote, Plus, Check, Trash2, Edit3, Send, BookOpen, WifiOff, Wifi,
  CloudSun, CloudRain, Cloud, Snowflake, Wind, ThermometerSun, Eye, EyeOff, Copy,
  Share2, Bookmark, Flag, StickyNote, ListTodo, CircleCheck, Circle, AlertOctagon
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { JewelModal, JewelInput, JewelTextarea, JewelSelect, JewelButton, JewelField, JewelChipRow } from '../../components/ui/jewel-modal';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { getImageUrl } from '../../utils/imageUtils';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Motivational quotes for the dashboard
const MOTIVATIONAL_QUOTES = [
  { quote: "Success is not final, failure is not fatal: it is the courage to continue that counts.", author: "Winston Churchill" },
  { quote: "The only way to do great work is to love what you do.", author: "Steve Jobs" },
  { quote: "Believe you can and you're halfway there.", author: "Theodore Roosevelt" },
  { quote: "Excellence is not a skill, it's an attitude.", author: "Ralph Marston" },
  { quote: "The future depends on what you do today.", author: "Mahatma Gandhi" },
  { quote: "Teamwork makes the dream work.", author: "John C. Maxwell" },
  { quote: "Your limitation—it's only your imagination.", author: "Unknown" },
  { quote: "Great things never come from comfort zones.", author: "Unknown" }
];

const DashboardPageRevamped = ({ getAuthHeaders, currentUser, setActivePage }) => {
  const navigate = useNavigate();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [clockStatus, setClockStatus] = useState({ is_clocked_in: false, elapsed_time: '0h 0m' });
  const [onBreak, setOnBreak] = useState(false);
  const [breakStartTime, setBreakStartTime] = useState(null);
  const [totalBreakMinutes, setTotalBreakMinutes] = useState(0);
  const [shiftNote, setShiftNote] = useState('');
  const [activeStaff, setActiveStaff] = useState([]);
  const [weeklyData, setWeeklyData] = useState({ chart_data: [], total_time: '0h 0m' });
  const [chatSessions, setChatSessions] = useState([]);
  const [userCount, setUserCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [teamUpdates, setTeamUpdates] = useState([]);
  const [showTeamUpdateModal, setShowTeamUpdateModal] = useState(false);
  const [newUpdate, setNewUpdate] = useState({ title: '', content: '', target_team: 'all', priority: 'normal', is_pinned: false });
  const [showInactivityModal, setShowInactivityModal] = useState(false);
  const [lastActivityTime, setLastActivityTime] = useState(Date.now());
  const [inactivityCountdown, setInactivityCountdown] = useState(600);
  const [showExtendedHistory, setShowExtendedHistory] = useState(false);
  const [extendedHistoryData, setExtendedHistoryData] = useState(null);
  const [historyMonths, setHistoryMonths] = useState(5);
  const [showDayModal, setShowDayModal] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [dayData, setDayData] = useState(null);
  const [trainingStats, setTrainingStats] = useState({ pending: 0, completed: 0 });
  const [kudosCount, setKudosCount] = useState(0);
  const [leaveBalance, setLeaveBalance] = useState({ annual: 0, sick: 0 });
  const [upcomingShifts, setUpcomingShifts] = useState([]);
  const [streak, setStreak] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // New state for additional features
  const [quickNotes, setQuickNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [todos, setTodos] = useState([]);
  const [newTodo, setNewTodo] = useState('');
  const [showTodoModal, setShowTodoModal] = useState(false);
  const [birthdays, setBirthdays] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [recentDocuments, setRecentDocuments] = useState([]);
  const [companyGoals, setCompanyGoals] = useState([]);
  const [dailyQuote, setDailyQuote] = useState(null);
  
  // Add modals
  const [showAddEventModal, setShowAddEventModal] = useState(false);
  const [newEvent, setNewEvent] = useState({ title: '', date: '', type: 'meeting' });
  const [showAddGoalModal, setShowAddGoalModal] = useState(false);
  const [newGoal, setNewGoal] = useState({ title: '', target: 100, current: 0, unit: '%' });
  const [showAddBirthdayModal, setShowAddBirthdayModal] = useState(false);
  const [newBirthday, setNewBirthday] = useState({ name: '', date: '' });
  const [chatAnalytics, setChatAnalytics] = useState(null);
  const [upcomingMeetings, setUpcomingMeetings] = useState([]);
  const [callHistory, setCallHistory] = useState([]);

  const isExecutive = ['superadmin', 'executive', 'admin'].includes(currentUser?.role);

  const inactivityTimerRef = React.useRef(null);
  const countdownTimerRef = React.useRef(null);
  const INACTIVITY_THRESHOLD = 30 * 60 * 1000;
  const RESPONSE_WINDOW = 10 * 60;

  const isManagement = ['superadmin', 'executive', 'admin', 'manager'].includes(currentUser?.role);

  // Initialize daily quote
  useEffect(() => {
    const today = new Date().toDateString();
    const storedQuote = localStorage.getItem('dailyQuote');
    const storedDate = localStorage.getItem('dailyQuoteDate');
    
    if (storedDate === today && storedQuote) {
      setDailyQuote(JSON.parse(storedQuote));
    } else {
      const randomQuote = MOTIVATIONAL_QUOTES[Math.floor(Math.random() * MOTIVATIONAL_QUOTES.length)];
      setDailyQuote(randomQuote);
      localStorage.setItem('dailyQuote', JSON.stringify(randomQuote));
      localStorage.setItem('dailyQuoteDate', today);
    }
  }, []);

  // Load saved notes and todos from localStorage
  useEffect(() => {
    const savedNotes = localStorage.getItem(`dashboard_notes_${currentUser?.username}`);
    const savedTodos = localStorage.getItem(`dashboard_todos_${currentUser?.username}`);
    if (savedNotes) setQuickNotes(JSON.parse(savedNotes));
    if (savedTodos) setTodos(JSON.parse(savedTodos));
  }, [currentUser?.username]);

  // Save notes to localStorage
  const saveNotes = (notes) => {
    localStorage.setItem(`dashboard_notes_${currentUser?.username}`, JSON.stringify(notes));
    setQuickNotes(notes);
  };

  // Save todos to localStorage
  const saveTodos = (todoList) => {
    localStorage.setItem(`dashboard_todos_${currentUser?.username}`, JSON.stringify(todoList));
    setTodos(todoList);
  };

  useEffect(() => {
    const clockInterval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(clockInterval);
  }, []);

  useEffect(() => {
    fetchAllData();
    const interval = setInterval(() => {
      fetchClockStatus();
      fetchActiveStaff();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!clockStatus.is_clocked_in) {
      if (inactivityTimerRef.current) clearInterval(inactivityTimerRef.current);
      if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
      setShowInactivityModal(false);
      return;
    }

    const handleActivity = () => {
      if (!showInactivityModal) setLastActivityTime(Date.now());
    };

    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(e => document.addEventListener(e, handleActivity, { passive: true }));

    inactivityTimerRef.current = setInterval(() => {
      if (Date.now() - lastActivityTime >= INACTIVITY_THRESHOLD && !showInactivityModal) {
        setInactivityCountdown(RESPONSE_WINDOW);
        setShowInactivityModal(true);
      }
    }, 60000);

    return () => {
      events.forEach(e => document.removeEventListener(e, handleActivity));
      if (inactivityTimerRef.current) clearInterval(inactivityTimerRef.current);
    };
  }, [clockStatus.is_clocked_in, lastActivityTime, showInactivityModal]);

  useEffect(() => {
    if (showInactivityModal) {
      countdownTimerRef.current = setInterval(() => {
        setInactivityCountdown(prev => {
          if (prev <= 1) { handleInactivityTimeout(); return 0; }
          return prev - 1;
        });
      }, 1000);
      return () => { if (countdownTimerRef.current) clearInterval(countdownTimerRef.current); };
    }
  }, [showInactivityModal]);

  const fetchAllData = async () => {
    const headers = getAuthHeaders();
    if (!headers) return;
    setIsRefreshing(true);

    try {
      const [chatRes, usersRes, clockRes, activeRes, weeklyRes, notifRes, updatesRes, trainingRes, kudosRes, leaveRes, shiftsRes, staffRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/chat/sessions`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/admin/user-accounts`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/clock/status`, { headers }).catch(() => ({ data: { is_clocked_in: false } })),
        axios.get(`${BACKEND_URL}/api/clock/active-staff`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/clock/weekly-summary`, { headers }).catch(() => ({ data: { chart_data: [], total_time: '0h 0m' } })),
        axios.get(`${BACKEND_URL}/api/notifications/dashboard`, { headers }).catch(() => ({ data: { notifications: [] } })),
        axios.get(`${BACKEND_URL}/api/team-updates/`, { headers }).catch(() => ({ data: { updates: [] } })),
        axios.get(`${BACKEND_URL}/api/training/my-assignments`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/staff-hub/stats`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${BACKEND_URL}/api/leave/balance`, { headers }).catch(() => ({ data: { annual_remaining: 0, sick_remaining: 0 } })),
        axios.get(`${BACKEND_URL}/api/shifts/upcoming`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/admin/staff`, { headers }).catch(() => ({ data: [] }))
      ]);

      setChatSessions(chatRes.data);
      setUserCount(usersRes.data.length);
      setClockStatus(clockRes.data);
      setActiveStaff(activeRes.data);
      setWeeklyData(weeklyRes.data);
      setNotifications(notifRes.data.notifications || []);
      setTeamUpdates(updatesRes.data.updates || []);
      
      const training = trainingRes.data;
      setTrainingStats({
        pending: training.filter(t => !t.passed).length,
        completed: training.filter(t => t.passed).length
      });
      
      setKudosCount(kudosRes.data.my_kudos_received || 0);
      setLeaveBalance({
        annual: leaveRes.data.annual_remaining || 0,
        sick: leaveRes.data.sick_remaining || 0
      });
      setUpcomingShifts(shiftsRes.data.slice(0, 3) || []);
      
      const workDays = weeklyRes.data.chart_data?.filter(d => d.hours > 0).length || 0;
      setStreak(workDays);

      // Fetch dashboard data from backend
      fetchDashboardData();

      // Fetch chat analytics and meetings
      try {
        const [analyticsRes, meetingsRes, historyRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/calling/chat-analytics`, { headers }).catch(() => ({ data: null })),
          axios.get(`${BACKEND_URL}/api/calling/meetings/upcoming`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/calling/history`, { headers }).catch(() => ({ data: [] }))
        ]);
        if (analyticsRes.data) setChatAnalytics(analyticsRes.data);
        setUpcomingMeetings(meetingsRes.data || []);
        setCallHistory(historyRes.data?.slice(0, 5) || []);
      } catch {}

    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const fetchDashboardData = async () => {
    const headers = getAuthHeaders();
    if (!headers) return;
    const [eventsRes, goalsRes, birthdaysRes] = await Promise.all([
      axios.get(`${BACKEND_URL}/api/dashboard/events`, { headers }).catch(() => ({ data: [] })),
      axios.get(`${BACKEND_URL}/api/dashboard/goals`, { headers }).catch(() => ({ data: [] })),
      axios.get(`${BACKEND_URL}/api/dashboard/birthdays`, { headers }).catch(() => ({ data: [] }))
    ]);
    setUpcomingEvents(eventsRes.data);
    setCompanyGoals(goalsRes.data);
    setBirthdays(birthdaysRes.data);
  };

  const fetchClockStatus = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const response = await axios.get(`${BACKEND_URL}/api/clock/status`, { headers });
      setClockStatus(response.data);
    } catch (error) {}
  };

  const fetchActiveStaff = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const response = await axios.get(`${BACKEND_URL}/api/clock/active-staff`, { headers });
      setActiveStaff(response.data);
    } catch (error) {}
  };

  const handleClockIn = async () => {
    try {
      const headers = getAuthHeaders();
      await axios.post(`${BACKEND_URL}/api/clock/in`, {}, { headers });
      toast.success('Clocked in — have a great shift!');
      fetchClockStatus();
      fetchActiveStaff();
      // Reset break tracking for the new shift
      const uname = currentUser?.username || 'anon';
      localStorage.removeItem(`shift_breaks_${uname}`);
      setOnBreak(false); setBreakStartTime(null); setTotalBreakMinutes(0);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clock in');
    }
  };

  const handleClockOut = async () => {
    try {
      const headers = getAuthHeaders();
      // End any active break first
      if (onBreak) toggleBreak();
      const response = await axios.post(`${BACKEND_URL}/api/clock/out`, {}, { headers });
      toast.success(`Shift complete · ${response.data.total_time}${totalBreakMinutes > 0 ? ` (${totalBreakMinutes}m break)` : ''}`);
      fetchClockStatus();
      fetchActiveStaff();
      fetchAllData();
      setShiftNote('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clock out');
    }
  };

  const toggleBreak = () => {
    const uname = currentUser?.username || 'anon';
    if (onBreak) {
      // End break
      const elapsed = Math.round((Date.now() - breakStartTime) / 60000);
      const newTotal = totalBreakMinutes + elapsed;
      setTotalBreakMinutes(newTotal);
      setOnBreak(false);
      setBreakStartTime(null);
      localStorage.setItem(`shift_breaks_${uname}`, JSON.stringify({ total: newTotal, activeStart: null }));
      toast.success(`Break ended · ${elapsed}m added (${newTotal}m total)`);
    } else {
      const now = Date.now();
      setOnBreak(true);
      setBreakStartTime(now);
      localStorage.setItem(`shift_breaks_${uname}`, JSON.stringify({ total: totalBreakMinutes, activeStart: now }));
      toast.success('Break started · take your time');
    }
  };

  // Restore break state on mount
  useEffect(() => {
    const uname = currentUser?.username || 'anon';
    const raw = localStorage.getItem(`shift_breaks_${uname}`);
    if (raw && clockStatus.is_clocked_in) {
      try {
        const d = JSON.parse(raw);
        setTotalBreakMinutes(d.total || 0);
        if (d.activeStart) { setOnBreak(true); setBreakStartTime(d.activeStart); }
      } catch {}
    }
  }, [currentUser?.username, clockStatus.is_clocked_in]);

  const handleForceClockOut = async (targetUsername) => {
    if (!window.confirm(`Force clock out ${targetUsername}?`)) return;
    try {
      const headers = getAuthHeaders();
      await axios.post(`${BACKEND_URL}/api/clock/force-out/${targetUsername}`, { reason: 'Forced clock out by management' }, { headers });
      toast.success(`${targetUsername} has been clocked out`);
      fetchActiveStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to force clock out');
    }
  };

  const handleConfirmActive = async () => {
    if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
    setShowInactivityModal(false);
    setLastActivityTime(Date.now());
    toast.success('Activity confirmed!');
  };

  const handleInactivityTimeout = async () => {
    if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
    if (inactivityTimerRef.current) clearInterval(inactivityTimerRef.current);
    setShowInactivityModal(false);
    
    try {
      const headers = getAuthHeaders();
      const totalInactiveMinutes = Math.ceil((Date.now() - lastActivityTime) / 60000);
      await axios.post(`${BACKEND_URL}/api/clock/inactivity-out`, {
        inactive_minutes: totalInactiveMinutes
      }, { headers });
      toast.warning(`Clocked out due to inactivity. ${totalInactiveMinutes} mins not counted.`);
      fetchClockStatus();
      fetchActiveStaff();
    } catch (error) {
      await handleClockOut();
    }
  };

  const handlePostUpdate = async () => {
    if (!newUpdate.title.trim() || !newUpdate.content.trim()) {
      toast.error('Please fill in title and content');
      return;
    }
    try {
      const headers = getAuthHeaders();
      await axios.post(`${BACKEND_URL}/api/team-updates/`, newUpdate, { headers });
      toast.success('Update posted!');
      setShowTeamUpdateModal(false);
      setNewUpdate({ title: '', content: '', target_team: 'all', priority: 'normal', is_pinned: false });
      fetchAllData();
    } catch (error) {
      toast.error('Failed to post update');
    }
  };

  const handleDeleteUpdate = async (updateId) => {
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${BACKEND_URL}/api/team-updates/${updateId}`, { headers });
      toast.success('Update deleted');
      setTeamUpdates(teamUpdates.filter(u => u.id !== updateId));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete update');
    }
  };

  // Events CRUD
  const handleAddEvent = async () => {
    if (!newEvent.title.trim() || !newEvent.date) { toast.error('Please fill in all fields'); return; }
    try {
      const headers = getAuthHeaders();
      await axios.post(`${BACKEND_URL}/api/dashboard/events`, newEvent, { headers });
      toast.success('Event added!');
      setShowAddEventModal(false);
      setNewEvent({ title: '', date: '', type: 'meeting' });
      fetchDashboardData();
    } catch (error) { toast.error('Failed to add event'); }
  };

  const handleDeleteEvent = async (eventId) => {
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${BACKEND_URL}/api/dashboard/events/${eventId}`, { headers });
      toast.success('Event deleted');
      setUpcomingEvents(upcomingEvents.filter(e => e.id !== eventId));
    } catch (error) { toast.error('Failed to delete event'); }
  };

  // Goals CRUD
  const handleAddGoal = async () => {
    if (!newGoal.title.trim()) { toast.error('Please enter a goal title'); return; }
    try {
      const headers = getAuthHeaders();
      await axios.post(`${BACKEND_URL}/api/dashboard/goals`, newGoal, { headers });
      toast.success('Goal added!');
      setShowAddGoalModal(false);
      setNewGoal({ title: '', target: 100, current: 0, unit: '%' });
      fetchDashboardData();
    } catch (error) { toast.error('Failed to add goal'); }
  };

  const handleDeleteGoal = async (goalId) => {
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${BACKEND_URL}/api/dashboard/goals/${goalId}`, { headers });
      toast.success('Goal deleted');
      setCompanyGoals(companyGoals.filter(g => g.id !== goalId));
    } catch (error) { toast.error('Failed to delete goal'); }
  };

  // Birthdays CRUD
  const handleAddBirthday = async () => {
    if (!newBirthday.name.trim() || !newBirthday.date) { toast.error('Please fill in all fields'); return; }
    try {
      const headers = getAuthHeaders();
      await axios.post(`${BACKEND_URL}/api/dashboard/birthdays`, newBirthday, { headers });
      toast.success('Birthday added!');
      setShowAddBirthdayModal(false);
      setNewBirthday({ name: '', date: '' });
      fetchDashboardData();
    } catch (error) { toast.error('Failed to add birthday'); }
  };

  const handleDeleteBirthday = async (birthdayId) => {
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${BACKEND_URL}/api/dashboard/birthdays/${birthdayId}`, { headers });
      toast.success('Birthday deleted');
      setBirthdays(birthdays.filter(b => b.id !== birthdayId));
    } catch (error) { toast.error('Failed to delete birthday'); }
  };

  const handleDayClick = async (date) => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${BACKEND_URL}/api/clock/day/${date}`, { headers });
      setDayData(response.data);
      setSelectedDay(date);
      setShowDayModal(true);
    } catch (error) {
      toast.error('Failed to load day data');
    }
  };

  const fetchExtendedHistory = async (months = 5) => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${BACKEND_URL}/api/clock/extended-history?months=${months}`, { headers });
      setExtendedHistoryData(response.data);
    } catch (error) {
      toast.error('Failed to load history');
    }
  };

  // Quick Notes Functions
  const addNote = () => {
    if (!newNote.trim()) return;
    const note = {
      id: Date.now(),
      text: newNote,
      createdAt: new Date().toISOString(),
      color: ['bg-yellow-500/20', 'bg-blue-500/20', 'bg-green-500/20', 'bg-pink-500/20', 'bg-purple-500/20'][Math.floor(Math.random() * 5)]
    };
    saveNotes([note, ...quickNotes]);
    setNewNote('');
    toast.success('Note added!');
  };

  const deleteNote = (id) => {
    saveNotes(quickNotes.filter(n => n.id !== id));
    toast.success('Note deleted');
  };

  // Todo Functions
  const addTodo = () => {
    if (!newTodo.trim()) return;
    const todo = {
      id: Date.now(),
      text: newTodo,
      completed: false,
      createdAt: new Date().toISOString(),
      priority: 'normal'
    };
    saveTodos([todo, ...todos]);
    setNewTodo('');
    toast.success('Task added!');
  };

  const toggleTodo = (id) => {
    saveTodos(todos.map(t => t.id === id ? { ...t, completed: !t.completed } : t));
  };

  const deleteTodo = (id) => {
    saveTodos(todos.filter(t => t.id !== id));
  };

  const formatCountdown = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;
  
  const getGreeting = () => {
    const h = currentTime.getHours();
    if (h < 12) return { text: 'Good Morning', icon: Sun, color: 'text-amber-400', bg: 'from-amber-500/10 to-orange-500/10' };
    if (h < 17) return { text: 'Good Afternoon', icon: Coffee, color: 'text-orange-400', bg: 'from-orange-500/10 to-red-500/10' };
    return { text: 'Good Evening', icon: Moon, color: 'text-indigo-400', bg: 'from-indigo-500/10 to-purple-500/10' };
  };

  const greeting = getGreeting();
  const activeChats = chatSessions.filter(s => s.status === 'active').length;
  const GreetIcon = greeting.icon;

  const navigateTo = (page) => {
    setActivePage(page);
    navigate(`/staff/${page}`);
  };

  // Calculate performance metrics
  const weeklyHours = parseFloat(weeklyData.total_time?.replace('h', '').replace('m', '') || 0);
  const targetHours = 40;
  const hoursProgress = Math.min((weeklyHours / targetHours) * 100, 100);

  const pendingTodos = todos.filter(t => !t.completed).length;
  const completedTodos = todos.filter(t => t.completed).length;

  return (
    <div className="space-y-6 max-w-[1600px] mx-auto" data-testid="dashboard-page">
      {/* ═══ PREMIUM HERO ═══ */}
      <div className="relative overflow-hidden rounded-3xl border border-white/[0.06] bg-gradient-to-br from-[#0a0d1a] via-[#0b0a15] to-[#0a0d1a] p-6 lg:p-8">
        <div className="absolute -top-24 -left-24 w-72 h-72 rounded-full blur-3xl opacity-25" style={{ background: 'radial-gradient(circle, #00E5FF 0%, transparent 70%)' }} />
        <div className="absolute -bottom-24 -right-24 w-72 h-72 rounded-full blur-3xl opacity-20" style={{ background: 'radial-gradient(circle, #8A2BE2 0%, transparent 70%)' }} />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)', backgroundSize: '32px 32px' }} />

        <div className="relative flex flex-col lg:flex-row items-start lg:items-center justify-between gap-5">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-white/50 text-xs uppercase tracking-[0.2em]">
              <GreetIcon className="w-3.5 h-3.5" style={{ color: '#00E5FF' }} />
              {greeting.text}
              {streak > 0 && (
                <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#F97316]/12 border border-[#F97316]/25 text-[#fdba74] normal-case tracking-normal text-[10px]">
                  <Flame className="w-3 h-3" /> {streak} day streak
                </span>
              )}
            </div>
            <h1 className="mt-2 text-3xl lg:text-4xl font-light text-white">
              Welcome back, <span className="italic font-serif" style={{ color: '#00E5FF' }}>{currentUser?.display_name?.split(' ')[0] || currentUser?.username}</span>
            </h1>
            <p className="mt-2 text-white/40 text-sm">
              {currentTime.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-3 bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl px-4 py-2.5">
              <Clock3 className="w-4 h-4 text-[#00E5FF]" />
              <span className="text-xl font-mono font-light text-white tracking-wider">
                {currentTime.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </span>
            </div>
            <Button
              onClick={fetchAllData}
              variant="outline"
              size="icon"
              className="bg-white/[0.02] border-white/10 text-white/70 hover:text-white hover:border-[#00E5FF]/30 hover:bg-white/[0.05] rounded-2xl"
              disabled={isRefreshing}
            >
              <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </div>

      {/* Daily Quote */}
      {dailyQuote && (
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-purple-900/30 via-indigo-900/20 to-blue-900/30 border border-purple-500/20 p-5">
          <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500/10 rounded-full blur-3xl" />
          <div className="relative flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center flex-shrink-0">
              <Quote className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <p className="text-white text-lg italic">"{dailyQuote.quote}"</p>
              <p className="text-purple-400 text-sm mt-2">— {dailyQuote.author}</p>
            </div>
          </div>
        </div>
      )}

      {/* ═══ PREMIUM QUICK ACTIONS ═══ */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="quick-actions">
        {[
          { id: 'live-chat',  label: 'Live Chat',  sub: `${chatSessions.length} active`, icon: MessageCircle, hex: '#00E5FF' },
          { id: 'conference', label: 'Conference', sub: 'Start meeting',                icon: Video,          hex: '#8A2BE2' },
          { id: 'staff-chat', label: 'Staff Chat', sub: 'Team messages',                icon: Users,          hex: '#10B981' },
          { id: 'wellbeing',  label: 'Wellbeing',  sub: 'Check-in & reset',              icon: Heart,          hex: '#F43F5E' },
          { id: 'staff-hub',  label: 'Staff Hub',  sub: 'Kudos & polls',                icon: Layers,         hex: '#F59E0B' },
        ].map(a => (
          <button
            key={a.id}
            onClick={() => setActivePage(a.id)}
            data-testid={`quick-action-${a.id}`}
            className="group relative overflow-hidden flex items-center gap-3 p-4 rounded-2xl bg-white/[0.02] border border-white/[0.06] hover:border-white/15 transition-all"
          >
            <span className="absolute inset-y-0 left-0 w-[3px]" style={{ background: `linear-gradient(to bottom, ${a.hex}, ${a.hex}00)` }} />
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${a.hex}18`, boxShadow: `0 0 20px ${a.hex}18` }}>
              <a.icon className="w-5 h-5" style={{ color: a.hex }} />
            </div>
            <div className="text-left min-w-0">
              <p className="text-white text-sm font-medium truncate">{a.label}</p>
              <p className="text-white/40 text-[10px] truncate">{a.sub}</p>
            </div>
            <ChevronRight className="ml-auto w-4 h-4 text-white/20 group-hover:text-white/60 transition-colors flex-shrink-0" />
          </button>
        ))}
      </div>

      {/* Performance Stats Row */}
      {chatAnalytics && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="performance-stats">
          <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Inbox className="w-4 h-4 text-cyan-400" />
              <span className="text-[10px] text-white/40 uppercase tracking-wider font-medium">Active Chats</span>
            </div>
            <p className="text-2xl font-bold text-white">{chatAnalytics.active_sessions}</p>
          </div>
          <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-4 h-4 text-emerald-400" />
              <span className="text-[10px] text-white/40 uppercase tracking-wider font-medium">Resolved Today</span>
            </div>
            <p className="text-2xl font-bold text-white">{chatAnalytics.resolved_today}</p>
          </div>
          <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-blue-400" />
              <span className="text-[10px] text-white/40 uppercase tracking-wider font-medium">This Week</span>
            </div>
            <p className="text-2xl font-bold text-white">{chatAnalytics.resolved_week}</p>
          </div>
          <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Star className="w-4 h-4 text-amber-400" />
              <span className="text-[10px] text-white/40 uppercase tracking-wider font-medium">Avg Rating</span>
            </div>
            <p className="text-2xl font-bold text-white">{chatAnalytics.avg_rating || '—'}<span className="text-sm text-white/40">/5</span></p>
          </div>
          <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Award className="w-4 h-4 text-purple-400" />
              <span className="text-[10px] text-white/40 uppercase tracking-wider font-medium">My Resolved</span>
            </div>
            <p className="text-2xl font-bold text-white">{chatAnalytics.my_resolved}</p>
          </div>
        </div>
      )}

      {/* Upcoming Meetings */}
      {upcomingMeetings.length > 0 && (
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-5" data-testid="upcoming-meetings">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold text-sm flex items-center gap-2"><CalendarDays className="w-4 h-4 text-purple-400" /> Upcoming Meetings</h3>
          </div>
          <div className="space-y-2">
            {upcomingMeetings.slice(0, 3).map(m => (
              <div key={m.id} className="flex items-center justify-between p-3 rounded-xl bg-white/[0.02] border border-white/[0.08]">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
                    <Video className="w-4 h-4 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-white text-xs font-medium">{m.title}</p>
                    <p className="text-white/40 text-[10px]">{new Date(m.scheduled_start).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                  </div>
                </div>
                <Badge className="bg-purple-500/10 text-purple-400 border-purple-500/20 text-[9px]">{m.call_type}</Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Main Clock In/Out Section */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Clock Status - Large Card */}
        <div className={`lg:col-span-2 relative overflow-hidden rounded-3xl border transition-all ${
          clockStatus.is_clocked_in
            ? onBreak
              ? 'bg-gradient-to-br from-[#F59E0B]/[0.08] via-[#F97316]/[0.04] to-[#0b0d1a] border-[#F59E0B]/30'
              : 'bg-gradient-to-br from-emerald-950/40 via-emerald-900/20 to-[#0b0d1a] border-emerald-500/30'
            : 'bg-gradient-to-br from-[#0d1020] via-[#0b0a15] to-[#0d1020] border-white/[0.06]'
        }`}>
          <div className="absolute -top-24 -left-24 w-72 h-72 rounded-full blur-3xl opacity-20" style={{ background: 'radial-gradient(circle, #00E5FF 0%, transparent 70%)' }} />
          <div className="absolute -bottom-24 -right-24 w-72 h-72 rounded-full blur-3xl opacity-20" style={{ background: 'radial-gradient(circle, #8A2BE2 0%, transparent 70%)' }} />
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)', backgroundSize: '32px 32px' }} />

          <div className="relative p-6 lg:p-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-5 w-full md:w-auto">
                <div className={`relative w-20 h-20 rounded-3xl flex items-center justify-center flex-shrink-0 ${
                  clockStatus.is_clocked_in
                    ? onBreak
                      ? 'bg-[#F59E0B]/15 border border-[#F59E0B]/30'
                      : 'bg-emerald-500/15 border border-emerald-500/25'
                    : 'bg-white/[0.03] border border-white/[0.08]'
                }`} style={clockStatus.is_clocked_in ? { boxShadow: onBreak ? '0 0 32px rgba(245,158,11,0.25)' : '0 0 32px rgba(16,185,129,0.25)' } : {}}>
                  {clockStatus.is_clocked_in ? (
                    <>
                      {onBreak ? <Coffee className="w-10 h-10 text-[#fbbf24]" /> : <Activity className="w-10 h-10 text-emerald-400" />}
                      <div className={`absolute inset-0 rounded-3xl border-2 animate-pulse ${onBreak ? 'border-[#F59E0B]/50' : 'border-emerald-500/50'}`} />
                    </>
                  ) : (
                    <Clock className="w-10 h-10 text-white/50" />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <p className="text-white/40 text-[10px] uppercase tracking-[0.2em] mb-1">Current status</p>
                  <h2 className="text-white font-light text-2xl mb-2">
                    {clockStatus.is_clocked_in ? (onBreak ? 'On break' : 'On shift') : 'Off shift'}
                  </h2>
                  {clockStatus.is_clocked_in ? (
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Timer className={`w-4 h-4 ${onBreak ? 'text-[#fbbf24]' : 'text-emerald-400'}`} />
                        <span className={`font-mono font-bold text-lg ${onBreak ? 'text-[#fbbf24]' : 'text-emerald-400'}`}>{clockStatus.elapsed_time}</span>
                        {totalBreakMinutes > 0 && (
                          <Badge className="bg-white/[0.04] border border-white/10 text-white/60 text-[10px]">
                            <Coffee className="w-2.5 h-2.5 mr-1" /> {totalBreakMinutes}m break
                          </Badge>
                        )}
                      </div>
                      {clockStatus.clock_in_time && (
                        <p className="text-white/40 text-sm">
                          Started {new Date(clockStatus.clock_in_time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })} · {activeStaff.length} on shift right now
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-white/40 text-sm">Clock in to start tracking your shift</p>
                  )}
                </div>
              </div>

              <div className="flex flex-col items-stretch gap-2 w-full md:w-auto">
                <button
                  onClick={clockStatus.is_clocked_in ? handleClockOut : handleClockIn}
                  data-testid="clock-button"
                  className={`group relative flex items-center justify-center gap-3 px-10 py-5 rounded-2xl font-medium text-lg transition-all hover:scale-[1.03] border ${
                    clockStatus.is_clocked_in
                      ? 'bg-gradient-to-r from-[#F43F5E]/25 to-[#F97316]/25 border-[#F43F5E]/40 text-white'
                      : 'bg-gradient-to-r from-emerald-500/25 to-[#00E5FF]/25 border-emerald-500/40 text-white'
                  }`}
                  style={{ boxShadow: clockStatus.is_clocked_in ? '0 0 32px rgba(244,63,94,0.2)' : '0 0 32px rgba(16,185,129,0.2)' }}
                >
                  {clockStatus.is_clocked_in ? <><Square className="w-6 h-6" /><span>Clock out</span></> : <><Play className="w-6 h-6" /><span>Clock in</span></>}
                </button>
                {clockStatus.is_clocked_in && (
                  <button
                    onClick={toggleBreak}
                    data-testid="break-toggle"
                    className={`flex items-center justify-center gap-2 px-6 py-2.5 rounded-full text-sm font-medium border transition-all ${
                      onBreak
                        ? 'bg-emerald-500/15 border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/25'
                        : 'bg-[#F59E0B]/10 border-[#F59E0B]/30 text-[#fbbf24] hover:bg-[#F59E0B]/20'
                    }`}
                  >
                    {onBreak ? <><Play className="w-4 h-4" /> Resume shift</> : <><Coffee className="w-4 h-4" /> Take a break</>}
                  </button>
                )}
              </div>
            </div>

            {/* Shift metrics rail */}
            {clockStatus.is_clocked_in && (
              <div className="mt-6 pt-6 border-t border-white/[0.05] grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: 'Today',      value: clockStatus.elapsed_time,                           hex: '#00E5FF', icon: Clock },
                  { label: 'This week',  value: weeklyData.total_time || '0h 0m',                    hex: '#8A2BE2', icon: BarChart3 },
                  { label: 'Break',      value: onBreak ? 'Live' : `${totalBreakMinutes}m`,          hex: '#F59E0B', icon: Coffee },
                  { label: 'Team',       value: `${activeStaff.length} live`,                        hex: '#10B981', icon: Users },
                ].map(m => (
                  <div key={m.label} className="p-3 rounded-xl bg-white/[0.02] border border-white/[0.06]">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] uppercase tracking-wider text-white/40">{m.label}</span>
                      <m.icon className="w-3.5 h-3.5" style={{ color: m.hex }} />
                    </div>
                    <div className="text-white font-mono font-light text-lg">{m.value}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Shift note (only visible when on shift) */}
            {clockStatus.is_clocked_in && (
              <div className="mt-4">
                <input
                  type="text"
                  value={shiftNote}
                  onChange={(e) => setShiftNote(e.target.value)}
                  placeholder="Shift note (visible on your clock-out receipt)..."
                  data-testid="shift-note-input"
                  className="w-full px-4 py-2.5 bg-white/[0.02] border border-white/[0.06] rounded-xl text-white placeholder-white/30 text-sm focus:outline-none focus:border-emerald-500/30"
                />
              </div>
            )}
          </div>
        </div>

        {/* Weekly Progress Card */}
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-3xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <PieChart className="w-5 h-5 text-cyan-400" />
              Weekly Progress
            </h3>
            <span className="text-white/40 text-sm">{weeklyData.total_time}</span>
          </div>
          
          <div className="flex justify-center mb-4">
            <div className="relative w-28 h-28">
              <svg className="w-full h-full transform -rotate-90">
                <circle cx="56" cy="56" r="48" stroke="currentColor" strokeWidth="10" fill="none" className="text-white/10" />
                <circle cx="56" cy="56" r="48" stroke="url(#progress-gradient)" strokeWidth="10" fill="none" strokeLinecap="round"
                  strokeDasharray={`${hoursProgress * 3.01} 301`} className="transition-all duration-1000" />
                <defs>
                  <linearGradient id="progress-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#22d3ee" />
                    <stop offset="100%" stopColor="#3b82f6" />
                  </linearGradient>
                </defs>
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-2xl font-bold text-white">{Math.round(hoursProgress)}%</span>
                <span className="text-[10px] text-white/40">of {targetHours}h</span>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-white/[0.02] rounded-xl p-2.5 text-center">
              <p className="text-xl font-bold text-white">{activeStaff.length}</p>
              <p className="text-[10px] text-white/40">Team Online</p>
            </div>
            <div className="bg-white/[0.02] rounded-xl p-2.5 text-center">
              <p className="text-xl font-bold text-cyan-400">{activeChats}</p>
              <p className="text-[10px] text-white/40">Active Chats</p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {[
          { label: 'Staff Online', value: activeStaff.length, icon: UserCheck, color: 'cyan', onClick: () => navigateTo('staff-directory') },
          { label: 'Active Chats', value: activeChats, icon: MessageCircle, color: 'yellow', badge: activeChats > 0, onClick: () => navigateTo('live-chat') },
          { label: 'Training', value: trainingStats.pending > 0 ? `${trainingStats.pending} pending` : `${trainingStats.completed} done`, icon: GraduationCap, color: trainingStats.pending > 0 ? 'orange' : 'purple', onClick: () => navigateTo('eportal') },
          { label: 'Leave Balance', value: `${leaveBalance.annual} days`, icon: Calendar, color: 'teal', onClick: () => navigateTo('leave-management') },
          { label: 'My Tasks', value: `${pendingTodos} pending`, icon: ListTodo, color: 'pink', onClick: () => setShowTodoModal(true) },
          { label: 'Quick Notes', value: `${quickNotes.length} notes`, icon: StickyNote, color: 'amber', onClick: () => setShowNotesModal(true) }
        ].map((stat, i) => (
          <button key={i} onClick={stat.onClick}
            className="group bg-white/[0.02] border border-white/[0.06] rounded-2xl p-4 text-left hover:border-white/[0.08] hover:bg-white/[0.02] transition-all">
            <div className="flex items-start justify-between mb-2">
              <div className={`w-9 h-9 rounded-xl bg-${stat.color}-500/20 flex items-center justify-center group-hover:scale-110 transition-transform`}>
                <stat.icon className={`w-4 h-4 text-${stat.color}-400`} />
              </div>
              {stat.badge && <span className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse" />}
            </div>
            <p className="text-white font-bold">{stat.value}</p>
            <p className="text-white/40 text-xs">{stat.label}</p>
          </button>
        ))}
      </div>

      {/* Main Content Grid - 3 Columns */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Team Updates */}
        <div className="lg:col-span-2 bg-white/[0.02] border border-white/[0.06] rounded-2xl overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b border-white/[0.06]">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <Megaphone className="w-5 h-5 text-blue-400" />
              Team Updates
              {teamUpdates.filter(u => !u.read).length > 0 && (
                <Badge className="bg-blue-500/20 text-blue-400 text-xs">{teamUpdates.filter(u => !u.read).length} new</Badge>
              )}
            </h3>
            {isManagement && (
              <Button onClick={() => setShowTeamUpdateModal(true)} size="sm" variant="ghost" className="text-blue-400 hover:bg-blue-500/20">
                <Plus className="w-4 h-4 mr-1" /> Post
              </Button>
            )}
          </div>
          
          <div className="p-4">
            {teamUpdates.length === 0 ? (
              <div className="text-center py-8 text-white/40">
                <Inbox className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No updates yet</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-64 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-slate-700">
                {teamUpdates.slice(0, 5).map((update) => (
                  <div key={update.id} className={`p-3 rounded-xl border transition-all group ${
                    update.read ? 'bg-white/[0.02] border-white/[0.06]' : 'bg-blue-900/20 border-blue-500/30'
                  }`}>
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        update.priority === 'urgent' ? 'bg-red-500/20' : 'bg-blue-500/20'
                      }`}>
                        {update.is_pinned ? <Pin className="w-4 h-4 text-amber-400" /> : <Megaphone className="w-4 h-4 text-blue-400" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-white font-medium text-sm truncate">{update.title}</h4>
                        <p className="text-white/50 text-xs line-clamp-2 mt-0.5">{update.content}</p>
                        <p className="text-white/30 text-[10px] mt-1">{update.author_name} • {new Date(update.created_at).toLocaleDateString()}</p>
                      </div>
                      {isExecutive && (
                        <button onClick={() => handleDeleteUpdate(update.id)}
                          className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-lg hover:bg-red-500/20 text-white/40 hover:text-red-400 flex-shrink-0">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Weekly Hours */}
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b border-white/[0.06]">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-purple-400" />
              This Week
            </h3>
            {isManagement && (
              <Button onClick={() => { setShowExtendedHistory(true); fetchExtendedHistory(historyMonths); }} size="sm" variant="ghost" className="text-purple-400 hover:bg-purple-500/20">
                <History className="w-4 h-4" />
              </Button>
            )}
          </div>
          
          <div className="p-4 space-y-1.5">
            {weeklyData.chart_data?.slice().reverse().map((day) => {
              const percentage = Math.min((day.hours / 10) * 100, 100);
              const isToday = day.date === new Date().toISOString().split('T')[0];
              return (
                <button key={day.date} onClick={() => handleDayClick(day.date)}
                  className={`w-full flex items-center gap-2 p-2 rounded-lg transition-all group ${isToday ? 'bg-purple-500/10' : 'hover:bg-white/[0.02]'}`}>
                  <span className={`text-[10px] w-8 font-semibold ${isToday ? 'text-purple-400' : 'text-white/40'}`}>{day.day}</span>
                  <div className="flex-1 bg-white/[0.02] rounded-full h-2 overflow-hidden">
                    <div className={`h-full rounded-full ${
                      day.hours >= 8 ? 'bg-gradient-to-r from-emerald-500 to-green-400' :
                      day.hours >= 4 ? 'bg-gradient-to-r from-cyan-500 to-blue-400' :
                      day.hours > 0 ? 'bg-gradient-to-r from-amber-500 to-orange-400' : 'bg-slate-700'
                    }`} style={{ width: `${percentage}%` }} />
                  </div>
                  <span className={`text-xs font-mono w-8 text-right ${day.hours >= 8 ? 'text-emerald-400' : 'text-white'}`}>{day.hours}h</span>
                </button>
              );
            })}
            <div className="mt-3 pt-3 border-t border-white/[0.06] flex items-center justify-between">
              <span className="text-white/40 text-xs">Total</span>
              <span className="text-white font-bold">{weeklyData.total_time}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Second Row - Birthdays, Events, Goals */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Upcoming Birthdays */}
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b border-white/[0.06]">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <Cake className="w-5 h-5 text-pink-400" />
              Upcoming Birthdays
            </h3>
            {isExecutive && (
              <Button onClick={() => setShowAddBirthdayModal(true)} size="sm" variant="ghost" className="text-pink-400 hover:bg-pink-500/20">
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            )}
          </div>
          <div className="p-4">
            {birthdays.length === 0 ? (
              <div className="text-center py-6 text-white/40">
                <Cake className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-xs">No upcoming birthdays</p>
              </div>
            ) : (
              <div className="space-y-2">
                {birthdays.map((person) => {
                  const bDate = new Date(person.date);
                  const today = new Date();
                  const thisYearBday = new Date(today.getFullYear(), bDate.getMonth(), bDate.getDate());
                  if (thisYearBday < today) thisYearBday.setFullYear(today.getFullYear() + 1);
                  const daysUntil = Math.ceil((thisYearBday - today) / (1000 * 60 * 60 * 24));
                  return (
                    <div key={person.id} className="flex items-center gap-3 p-2 rounded-lg bg-white/[0.02] group">
                      <div className="w-8 h-8 rounded-full bg-pink-500/20 flex items-center justify-center">
                        <span className="text-pink-400 text-xs font-bold">{person.name?.charAt(0) || '?'}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm truncate">{person.name}</p>
                        <p className="text-white/40 text-[10px]">
                          {bDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                          {daysUntil === 0 ? ' - Today!' : ` - In ${daysUntil} days`}
                        </p>
                      </div>
                      {daysUntil === 0 && <Gift className="w-4 h-4 text-amber-400" />}
                      {isExecutive && (
                        <button onClick={() => handleDeleteBirthday(person.id)}
                          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-lg hover:bg-red-500/20 text-white/40 hover:text-red-400">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Upcoming Events */}
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b border-white/[0.06]">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-teal-400" />
              Upcoming Events
            </h3>
            {isExecutive && (
              <Button onClick={() => setShowAddEventModal(true)} size="sm" variant="ghost" className="text-teal-400 hover:bg-teal-500/20">
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            )}
          </div>
          <div className="p-4">
            {upcomingEvents.length === 0 ? (
              <div className="text-center py-6 text-white/40">
                <CalendarDays className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-xs">No upcoming events</p>
              </div>
            ) : (
              <div className="space-y-2">
                {upcomingEvents.map((event) => (
                  <div key={event.id} className="flex items-center gap-3 p-2 rounded-lg bg-white/[0.02] group">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      event.type === 'meeting' ? 'bg-blue-500/20' :
                      event.type === 'training' ? 'bg-purple-500/20' : 'bg-orange-500/20'
                    }`}>
                      {event.type === 'meeting' ? <Users className="w-4 h-4 text-blue-400" /> :
                       event.type === 'training' ? <GraduationCap className="w-4 h-4 text-purple-400" /> :
                       <ClipboardList className="w-4 h-4 text-orange-400" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm truncate">{event.title}</p>
                      <p className="text-white/40 text-[10px]">{new Date(event.date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}</p>
                    </div>
                    {isExecutive && (
                      <button onClick={() => handleDeleteEvent(event.id)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-lg hover:bg-red-500/20 text-white/40 hover:text-red-400">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Company Goals */}
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b border-white/[0.06]">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <Target className="w-5 h-5 text-amber-400" />
              Company Goals
            </h3>
            {isExecutive && (
              <Button onClick={() => setShowAddGoalModal(true)} size="sm" variant="ghost" className="text-amber-400 hover:bg-amber-500/20">
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            )}
          </div>
          <div className="p-4 space-y-3">
            {companyGoals.length === 0 ? (
              <div className="text-center py-6 text-white/40">
                <Target className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-xs">No company goals set</p>
              </div>
            ) : (
              companyGoals.map((goal) => {
                const progress = Math.min((goal.current / goal.target) * 100, 100);
                const isAchieved = goal.current >= goal.target;
                return (
                  <div key={goal.id} className="space-y-1.5 group">
                    <div className="flex items-center justify-between">
                      <span className="text-white text-sm">{goal.title}</span>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-mono ${isAchieved ? 'text-emerald-400' : 'text-white/50'}`}>
                          {goal.current}{goal.unit} / {goal.target}{goal.unit}
                        </span>
                        {isExecutive && (
                          <button onClick={() => handleDeleteGoal(goal.id)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-lg hover:bg-red-500/20 text-white/40 hover:text-red-400">
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="h-2 bg-white/[0.02] rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${
                        isAchieved ? 'bg-gradient-to-r from-emerald-500 to-green-400' :
                        progress >= 70 ? 'bg-gradient-to-r from-cyan-500 to-blue-400' :
                        'bg-gradient-to-r from-amber-500 to-orange-400'
                      }`} style={{ width: `${progress}%` }} />
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Staff On Shift */}
      <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-white/[0.06]">
          <h3 className="text-white font-light text-base flex items-center gap-2">
            <div className="relative">
              <UserCheck className="w-5 h-5 text-emerald-400" />
              {activeStaff.length > 0 && <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />}
            </div>
            <span>Currently on shift</span>
            <Badge className="bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[10px] ml-1">{activeStaff.length} live</Badge>
            {activeStaff.some(s => s.is_inactive) && (
              <Badge className="bg-[#F97316]/10 border border-[#F97316]/30 text-[#fdba74] text-[10px]">
                <AlertCircle className="w-2.5 h-2.5 mr-1" /> {activeStaff.filter(s => s.is_inactive).length} idle
              </Badge>
            )}
          </h3>
          <Button onClick={fetchActiveStaff} size="sm" variant="ghost" className="text-white/50 hover:text-white hover:bg-white/[0.05] rounded-full h-8">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
        </div>

        <div className="p-4">
          {activeStaff.length === 0 ? (
            <div className="text-center py-10 rounded-xl bg-white/[0.01] border border-dashed border-white/[0.06]">
              <UserCheck className="w-10 h-10 mx-auto mb-2 text-white/20" />
              <p className="text-sm text-white/40">No one is on shift yet — be the first to clock in.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {[...activeStaff].sort((a, b) => {
                // Longest shift first
                const parseElapsed = (t) => {
                  if (!t) return 0;
                  const h = parseInt((t.match(/(\d+)h/) || [0,0])[1]);
                  const m = parseInt((t.match(/(\d+)m/) || [0,0])[1]);
                  return h * 60 + m;
                };
                return parseElapsed(b.elapsed_time) - parseElapsed(a.elapsed_time);
              }).slice(0, 8).map((staff, idx) => (
                <div key={staff.username} className={`relative flex items-center gap-3 p-3 rounded-xl border transition-all group ${
                  staff.is_inactive ? 'bg-[#F97316]/[0.06] border-[#F97316]/25' : 'bg-white/[0.02] border-white/[0.06] hover:border-emerald-500/25'
                }`}>
                  {idx === 0 && !staff.is_inactive && activeStaff.length > 1 && (
                    <span className="absolute -top-1.5 -left-1.5 text-[9px] px-1.5 py-0.5 rounded-full bg-[#F59E0B]/20 border border-[#F59E0B]/40 text-[#fbbf24] font-medium uppercase tracking-wider" title="Longest shift right now">
                      Marathon
                    </span>
                  )}
                  <div className="relative flex-shrink-0">
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#00E5FF]/25 to-[#8A2BE2]/25 flex items-center justify-center overflow-hidden ring-1 ring-white/10">
                      {staff.avatar ? <img src={getImageUrl(staff.avatar)} alt="" className="w-full h-full object-cover" /> :
                        <span className="text-sm font-medium text-white">{staff.display_name?.charAt(0) || 'S'}</span>}
                    </div>
                    <div className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-[#05050A] ${staff.is_inactive ? 'bg-[#F97316]' : 'bg-emerald-500'} ${staff.is_inactive ? '' : 'animate-pulse'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">
                      {staff.display_name || staff.username}
                      {staff.username === currentUser?.username && <span className="text-white/40 text-xs font-normal ml-1">(you)</span>}
                    </p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <Clock className="w-3 h-3 text-emerald-400/60" />
                      <p className="text-emerald-400 text-xs font-mono">{staff.elapsed_time}</p>
                      {staff.is_inactive && <span className="text-[#fdba74] text-[10px]">· idle</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {staff.username !== currentUser?.username && (
                      <button
                        onClick={() => { setActivePage && setActivePage('staff-chat'); }}
                        data-testid={`msg-${staff.username}`}
                        className="w-7 h-7 rounded-lg flex items-center justify-center text-white/40 hover:text-[#00E5FF] hover:bg-[#00E5FF]/10 transition-all"
                        title="Send message"
                      >
                        <MessageCircle className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {isExecutive && staff.username !== currentUser?.username && (
                      <button
                        onClick={() => handleForceClockOut(staff.username)}
                        data-testid={`force-clockout-${staff.username}`}
                        className="w-7 h-7 rounded-lg flex items-center justify-center text-white/40 hover:text-[#F43F5E] hover:bg-[#F43F5E]/10 transition-all"
                        title="Force clock out"
                      >
                        <LogOut className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          {activeStaff.length > 8 && (
            <div className="mt-3 text-center">
              <span className="text-white/40 text-xs">+ {activeStaff.length - 8} more staff on shift</span>
            </div>
          )}
        </div>
      </div>

      {/* Quick Notes Modal */}
      <Dialog open={showNotesModal} onOpenChange={setShowNotesModal}>
        <DialogContent className="bg-[#0a0d1a] border-white/[0.08] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <StickyNote className="w-5 h-5 text-[#F59E0B]" />
              Quick Notes
              {quickNotes.length > 0 && <Badge className="bg-[#F59E0B]/10 border-[#F59E0B]/30 text-[#fbbf24] text-xs">{quickNotes.length}</Badge>}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="flex gap-2">
              <input type="text" value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Add a quick note..."
                onKeyPress={(e) => e.key === 'Enter' && addNote()}
                data-testid="quicknote-input"
                className="flex-1 px-4 py-2.5 bg-white/[0.02] border border-white/[0.06] rounded-xl text-white placeholder-white/30 text-sm focus:outline-none focus:border-[#F59E0B]/30" />
              <Button onClick={addNote} data-testid="quicknote-add" className="bg-gradient-to-r from-[#F59E0B]/25 to-[#F97316]/25 border border-[#F59E0B]/30 text-white rounded-full">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
              {quickNotes.length === 0 ? (
                <div className="p-6 rounded-xl bg-white/[0.02] border border-dashed border-white/[0.08] text-center">
                  <StickyNote className="w-6 h-6 mx-auto text-white/30 mb-2" />
                  <p className="text-white/40 text-sm">No notes yet. Add your first one.</p>
                </div>
              ) : (
                quickNotes.map((note) => (
                  <div key={note.id} className="p-3 rounded-xl bg-white/[0.02] border border-white/[0.06] group hover:border-[#F59E0B]/25 transition-all">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-white/90 text-sm flex-1 leading-relaxed">{note.text}</p>
                      <button onClick={() => deleteNote(note.id)} data-testid={`quicknote-delete-${note.id}`} className="w-7 h-7 rounded-lg flex items-center justify-center text-white/30 hover:text-[#F43F5E] hover:bg-[#F43F5E]/10 opacity-0 group-hover:opacity-100 transition-all flex-shrink-0">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <p className="text-white/30 text-[10px] mt-1.5">{new Date(note.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</p>
                  </div>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Todo Modal */}
      <Dialog open={showTodoModal} onOpenChange={setShowTodoModal}>
        <DialogContent className="bg-[#0a0d1a] border-white/[0.08] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ListTodo className="w-5 h-5 text-[#00E5FF]" />
              My Tasks
              {pendingTodos > 0 && <Badge className="bg-[#00E5FF]/10 border-[#00E5FF]/30 text-[#00E5FF] text-xs">{pendingTodos} pending</Badge>}
              {completedTodos > 0 && <Badge className="bg-emerald-500/10 border-emerald-500/30 text-emerald-300 text-xs">{completedTodos} done</Badge>}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="flex gap-2">
              <input type="text" value={newTodo} onChange={(e) => setNewTodo(e.target.value)} placeholder="Add a new task..."
                onKeyPress={(e) => e.key === 'Enter' && addTodo()}
                data-testid="todo-input"
                className="flex-1 px-4 py-2.5 bg-white/[0.02] border border-white/[0.06] rounded-xl text-white placeholder-white/30 text-sm focus:outline-none focus:border-[#00E5FF]/30" />
              <Button onClick={addTodo} data-testid="todo-add" className="bg-gradient-to-r from-[#00E5FF]/25 to-[#8A2BE2]/25 border border-[#00E5FF]/30 text-white rounded-full">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {todos.length > 0 && (
              <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-white/40 px-1">
                <span>{completedTodos} of {todos.length} complete</span>
                <div className="flex-1 mx-3 h-1 rounded-full bg-white/[0.04] overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2] transition-all" style={{ width: `${todos.length ? (completedTodos / todos.length) * 100 : 0}%` }} />
                </div>
                <span>{todos.length ? Math.round((completedTodos / todos.length) * 100) : 0}%</span>
              </div>
            )}
            <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
              {todos.length === 0 ? (
                <div className="p-6 rounded-xl bg-white/[0.02] border border-dashed border-white/[0.08] text-center">
                  <ListTodo className="w-6 h-6 mx-auto text-white/30 mb-2" />
                  <p className="text-white/40 text-sm">No tasks yet. Add your first one.</p>
                </div>
              ) : (
                todos.map((todo) => (
                  <div key={todo.id} className={`flex items-center gap-3 p-3 rounded-xl border transition-all group ${
                    todo.completed ? 'bg-emerald-500/[0.05] border-emerald-500/20' : 'bg-white/[0.02] border-white/[0.06] hover:border-white/15'
                  }`}>
                    <button onClick={() => toggleTodo(todo.id)} data-testid={`todo-toggle-${todo.id}`} className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                      todo.completed ? 'bg-emerald-500 border-emerald-500' : 'border-white/20 hover:border-[#00E5FF]/50'
                    }`}>
                      {todo.completed && <Check className="w-3 h-3 text-white" />}
                    </button>
                    <span className={`flex-1 text-sm ${todo.completed ? 'text-white/40 line-through' : 'text-white/90'}`}>{todo.text}</span>
                    <button onClick={() => deleteTodo(todo.id)} data-testid={`todo-delete-${todo.id}`} className="w-7 h-7 rounded-lg flex items-center justify-center text-white/30 hover:text-[#F43F5E] hover:bg-[#F43F5E]/10 opacity-0 group-hover:opacity-100 transition-all">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Post Team Update Modal */}
      <Dialog open={showTeamUpdateModal} onOpenChange={setShowTeamUpdateModal}>
        <DialogContent className="bg-[#0a0d1a] border-white/[0.08] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Megaphone className="w-5 h-5 text-[#8A2BE2]" />
              Post Team Update
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <div className="text-xs uppercase tracking-wider text-white/50 mb-2">Title</div>
              <input type="text" value={newUpdate.title} onChange={(e) => setNewUpdate({ ...newUpdate, title: e.target.value })}
                placeholder="A short, clear headline"
                className="w-full px-4 py-3 bg-white/[0.02] border border-white/[0.06] rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-[#8A2BE2]/30" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-white/50 mb-2">Content</div>
              <textarea value={newUpdate.content} onChange={(e) => setNewUpdate({ ...newUpdate, content: e.target.value })}
                rows={4} placeholder="Tell your team what changed and what to do next..."
                className="w-full px-4 py-3 bg-white/[0.02] border border-white/[0.06] rounded-xl text-white placeholder-white/30 resize-none focus:outline-none focus:border-[#8A2BE2]/30" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-xs uppercase tracking-wider text-white/50 mb-2">Team</div>
                <select value={newUpdate.target_team} onChange={(e) => setNewUpdate({ ...newUpdate, target_team: e.target.value })}
                  className="w-full px-4 py-3 bg-white/[0.02] border border-white/[0.06] rounded-xl text-white">
                  <option value="all">All Teams</option>
                  <option value="operations">Operations</option>
                  <option value="peoples_team">People Team</option>
                </select>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-white/50 mb-2">Priority</div>
                <select value={newUpdate.priority} onChange={(e) => setNewUpdate({ ...newUpdate, priority: e.target.value })}
                  className="w-full px-4 py-3 bg-white/[0.02] border border-white/[0.06] rounded-xl text-white">
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
            </div>
            <label className="flex items-center gap-3 cursor-pointer">
              <div
                onClick={() => setNewUpdate({ ...newUpdate, is_pinned: !newUpdate.is_pinned })}
                className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                  newUpdate.is_pinned ? 'bg-[#8A2BE2]/30 border-[#8A2BE2]/60' : 'bg-white/[0.02] border-white/[0.06]'
                }`}
              >
                {newUpdate.is_pinned && <Check className="w-3 h-3 text-[#a673e8]" />}
              </div>
              <span className="text-white/60 text-sm">Pin this update to the top</span>
            </label>
            <div className="flex gap-3 pt-2">
              <Button onClick={() => setShowTeamUpdateModal(false)} variant="outline" className="flex-1 bg-white/[0.02] border-white/10 text-white/70">Cancel</Button>
              <Button onClick={handlePostUpdate} data-testid="team-update-post" className="flex-1 bg-gradient-to-r from-[#8A2BE2]/30 to-[#00E5FF]/30 border border-[#8A2BE2]/30 text-white">
                <Send className="w-4 h-4 mr-2" />
                Post update
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Day Details Modal */}
      <Dialog open={showDayModal} onOpenChange={setShowDayModal}>
        <DialogContent className="bg-[#0a0d1a] border-white/[0.08] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-cyan-400" />
              {selectedDay && new Date(selectedDay).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
            </DialogTitle>
          </DialogHeader>
          {dayData && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-white/[0.02] rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold text-white">{dayData.summary?.total_staff || 0}</p>
                  <p className="text-xs text-white/40">Staff</p>
                </div>
                <div className="bg-white/[0.02] rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold text-cyan-400">{dayData.summary?.total_hours || 0}</p>
                  <p className="text-xs text-white/40">Hours</p>
                </div>
                <div className="bg-white/[0.02] rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold text-orange-400">{dayData.summary?.overtime_count || 0}</p>
                  <p className="text-xs text-white/40">Overtime</p>
                </div>
              </div>
              <div className="max-h-48 overflow-y-auto space-y-2">
                {dayData.records?.map((record, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-white/[0.02] rounded-xl">
                    <div>
                      <p className="text-white font-medium text-sm">{record.display_name}</p>
                      <p className="text-white/40 text-xs">
                        {new Date(record.clock_in_time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })} - 
                        {record.clock_out_time ? new Date(record.clock_out_time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : 'Active'}
                      </p>
                    </div>
                    <p className="text-cyan-400 font-mono">{record.total_time}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Extended History Modal */}
      <Dialog open={showExtendedHistory} onOpenChange={setShowExtendedHistory}>
        <DialogContent className="bg-[#0a0d1a] border-white/[0.08] text-white max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                <History className="w-5 h-5 text-purple-400" />
                Staff Hours History
              </DialogTitle>
              <select value={historyMonths} onChange={(e) => { setHistoryMonths(parseInt(e.target.value)); fetchExtendedHistory(parseInt(e.target.value)); }}
                className="px-3 py-1.5 bg-white/[0.02] border border-white/[0.08] rounded-lg text-white text-sm">
                <option value={3}>3 Months</option>
                <option value={5}>5 Months</option>
                <option value={6}>6 Months</option>
                <option value={12}>12 Months</option>
              </select>
            </div>
          </DialogHeader>
          <div className="space-y-3 mt-4">
            {extendedHistoryData?.staff_data?.map((staff) => (
              <div key={staff.username} className="p-4 bg-white/[0.02] rounded-xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-purple-500/30 flex items-center justify-center">
                      <span className="text-white font-bold">{staff.display_name?.charAt(0) || 'S'}</span>
                    </div>
                    <div>
                      <p className="text-white font-medium">{staff.display_name}</p>
                      <p className="text-white/40 text-xs">{staff.total_shifts} shifts</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-cyan-400">{staff.total_time}</p>
                    <p className="text-white/40 text-xs">Avg: {staff.avg_monthly_hours}h/month</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Event Modal — Jewel */}
      <JewelModal
        open={showAddEventModal}
        onOpenChange={setShowAddEventModal}
        icon={CalendarDays}
        title="Add Event"
        subtitle="Schedule a moment worth remembering"
        accent="teal"
        size="md"
        testid="add-event-modal"
        footer={<>
          <JewelButton variant="ghost" onClick={() => setShowAddEventModal(false)} data-testid="add-event-cancel">Cancel</JewelButton>
          <JewelButton accent="teal" onClick={handleAddEvent} data-testid="add-event-submit">Add event</JewelButton>
        </>}
      >
        <div className="space-y-4">
          <JewelField label="Event title" required>
            <JewelInput
              value={newEvent.title}
              onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
              placeholder="Team standup, product launch, retreat…"
              data-testid="add-event-title"
              autoFocus
            />
          </JewelField>

          <div className="grid grid-cols-2 gap-3">
            <JewelField label="Date" required>
              <JewelInput
                type="date"
                value={newEvent.date}
                onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })}
                data-testid="add-event-date"
              />
            </JewelField>
            <JewelField label="Time (optional)">
              <JewelInput
                type="time"
                value={newEvent.time || ''}
                onChange={(e) => setNewEvent({ ...newEvent, time: e.target.value })}
                data-testid="add-event-time"
              />
            </JewelField>
          </div>

          <JewelField label="Category">
            <div className="grid grid-cols-3 gap-2">
              {[
                { v: 'meeting', label: 'Meeting', hex: '#00E5FF', icon: Users },
                { v: 'training', label: 'Training', hex: '#8A2BE2', icon: GraduationCap },
                { v: 'review', label: 'Review', hex: '#F59E0B', icon: CheckCircle },
                { v: 'oneonone', label: '1-on-1', hex: '#10B981', icon: MessageCircle },
                { v: 'social', label: 'Social', hex: '#EC4899', icon: PartyPopper },
                { v: 'deadline', label: 'Deadline', hex: '#F43F5E', icon: Clock },
              ].map(t => {
                const Icon = t.icon;
                const active = newEvent.type === t.v;
                return (
                  <button
                    key={t.v}
                    type="button"
                    onClick={() => setNewEvent({ ...newEvent, type: t.v })}
                    data-testid={`add-event-cat-${t.v}`}
                    className="group relative p-2.5 rounded-xl border transition-all flex flex-col items-center gap-1.5"
                    style={{
                      background: active ? `${t.hex}18` : 'rgba(255,255,255,0.02)',
                      borderColor: active ? `${t.hex}55` : 'rgba(255,255,255,0.06)',
                      boxShadow: active ? `0 0 20px -6px ${t.hex}55` : 'none',
                    }}
                  >
                    <Icon className="w-4 h-4" style={{ color: active ? t.hex : 'rgba(255,255,255,0.4)' }} />
                    <span className="text-[10px] font-medium tracking-wide" style={{ color: active ? t.hex : 'rgba(255,255,255,0.55)' }}>{t.label}</span>
                  </button>
                );
              })}
            </div>
          </JewelField>

          <JewelField label="Notes (optional)">
            <JewelTextarea
              value={newEvent.notes || ''}
              onChange={(e) => setNewEvent({ ...newEvent, notes: e.target.value })}
              placeholder="Location, attendees, or agenda…"
              rows={2}
              data-testid="add-event-notes"
            />
          </JewelField>

          <label className="flex items-center gap-3 cursor-pointer text-sm text-white/60 pt-1">
            <div
              onClick={() => setNewEvent({ ...newEvent, recurring: !newEvent.recurring })}
              className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                newEvent.recurring ? 'bg-teal-500/30 border-teal-400/60' : 'bg-white/[0.02] border-white/[0.06]'
              }`}
            >
              {newEvent.recurring && <Check className="w-3 h-3 text-teal-300" />}
            </div>
            <span>Repeat weekly on the same day</span>
          </label>
        </div>
      </JewelModal>

      {/* Add Goal Modal — Jewel */}
      <JewelModal
        open={showAddGoalModal}
        onOpenChange={setShowAddGoalModal}
        icon={Target}
        title="Add Goal"
        subtitle="Set the north star for your team"
        accent="amber"
        size="md"
        testid="add-goal-modal"
        footer={<>
          <JewelButton variant="ghost" onClick={() => setShowAddGoalModal(false)}>Cancel</JewelButton>
          <JewelButton accent="amber" onClick={handleAddGoal} data-testid="add-goal-submit">Add goal</JewelButton>
        </>}
      >
        <div className="space-y-4">
          <JewelField label="Goal name" required>
            <JewelInput
              value={newGoal.title}
              onChange={(e) => setNewGoal({ ...newGoal, title: e.target.value })}
              placeholder="Customer satisfaction, revenue growth…"
              autoFocus
            />
          </JewelField>
          <div className="grid grid-cols-3 gap-3">
            <JewelField label="Target">
              <JewelInput type="number" value={newGoal.target} onChange={(e) => setNewGoal({ ...newGoal, target: parseFloat(e.target.value) || 0 })} />
            </JewelField>
            <JewelField label="Current">
              <JewelInput type="number" value={newGoal.current} onChange={(e) => setNewGoal({ ...newGoal, current: parseFloat(e.target.value) || 0 })} />
            </JewelField>
            <JewelField label="Unit">
              <JewelSelect value={newGoal.unit} onChange={(e) => setNewGoal({ ...newGoal, unit: e.target.value })}>
                <option value="%">%</option>
                <option value="K">K</option>
                <option value="min">min</option>
                <option value="hrs">hrs</option>
                <option value="pts">pts</option>
              </JewelSelect>
            </JewelField>
          </div>
          {/* Progress preview */}
          <div className="p-3 rounded-xl bg-white/[0.02] border border-white/[0.06]">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] tracking-[0.15em] uppercase text-white/40">Live preview</span>
              <span className="text-amber-300 text-xs font-mono">{newGoal.current || 0}/{newGoal.target || 0} {newGoal.unit}</span>
            </div>
            <div className="h-2 rounded-full bg-white/[0.04] overflow-hidden">
              <div className="h-full rounded-full bg-gradient-to-r from-amber-400 to-orange-500 transition-all"
                style={{ width: `${Math.min(100, ((newGoal.current || 0) / (newGoal.target || 1)) * 100)}%` }} />
            </div>
          </div>
        </div>
      </JewelModal>

      {/* Add Birthday Modal — Jewel */}
      <JewelModal
        open={showAddBirthdayModal}
        onOpenChange={setShowAddBirthdayModal}
        icon={Cake}
        title="Add Birthday"
        subtitle="Never miss a celebration"
        accent="pink"
        size="md"
        testid="add-birthday-modal"
        footer={<>
          <JewelButton variant="ghost" onClick={() => setShowAddBirthdayModal(false)}>Cancel</JewelButton>
          <JewelButton accent="pink" onClick={handleAddBirthday} data-testid="add-birthday-submit">Add birthday</JewelButton>
        </>}
      >
        <div className="space-y-4">
          <JewelField label="Name" required>
            <JewelInput
              value={newBirthday.name}
              onChange={(e) => setNewBirthday({ ...newBirthday, name: e.target.value })}
              placeholder="Who is celebrating?"
              autoFocus
            />
          </JewelField>
          <JewelField label="Date" required>
            <JewelInput
              type="date"
              value={newBirthday.date}
              onChange={(e) => setNewBirthday({ ...newBirthday, date: e.target.value })}
            />
          </JewelField>
        </div>
      </JewelModal>

      {/* Inactivity Modal */}
      <Dialog open={showInactivityModal} onOpenChange={() => {}}>
        <DialogContent className="bg-[#0a0d1a] border-yellow-500/50 text-white max-w-md" hideClose>
          <div className="text-center py-4">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-yellow-500/20 flex items-center justify-center">
              <AlertCircle className="w-10 h-10 text-yellow-400 animate-bounce" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Are You Still Working?</h2>
            <p className="text-white/50 mb-4">You've been inactive for a while.</p>
            
            <div className="bg-red-900/30 border border-red-500/50 rounded-xl p-4 mb-6">
              <p className="text-red-300 text-sm mb-1">Auto clock-out in:</p>
              <p className="text-4xl font-mono font-bold text-red-400">{formatCountdown(inactivityCountdown)}</p>
            </div>
            
            <div className="flex flex-col gap-3">
              <Button onClick={handleConfirmActive} className="w-full bg-green-500 hover:bg-green-600 py-4 text-lg">
                <CheckCircle className="w-5 h-5 mr-2" />
                Yes, I'm Working
              </Button>
              <Button onClick={handleInactivityTimeout} variant="outline" className="w-full border-red-500/50 text-red-400 hover:bg-red-500/10 py-4">
                <LogOut className="w-5 h-5 mr-2" />
                Clock Me Out
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DashboardPageRevamped;
